import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ChevronDown, Shield, Sparkles } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

/**
 * "Why this routine?" — transparency + safety disclosure.
 *
 * Hidden AI is broken trust. This panel tells the user, plainly:
 *   • exactly what inputs we sent the digital drag mom (Trouvé)
 *   • that GPT-4o Vision generated the routine
 *   • that every word is filtered through TruBeauté's safety system
 *
 * Particularly important for the privacy-cautious audience this app serves.
 *
 * Static fallback labels live in en.json (`routineDisclosure.value*`)
 * and the value below; when a translation key exists for the chosen
 * goal/experience/coverage/etc. it overrides the English value.
 */

const useEnumLabels = (t) => ({
  goal: {
    soft: t("routineDisclosure.goalSoft", "Soft radiance"),
    everyday: t("routineDisclosure.goalEveryday", "Everyday beauty"),
    glam: t("routineDisclosure.goalGlam", "Full glam"),
  },
  experience: {
    beginner: t("routineDisclosure.expBeginner", "Just starting out"),
    some: t("routineDisclosure.expSome", "Finding my rhythm"),
    confident: t("routineDisclosure.expConfident", "Already glowing"),
  },
  coverage: {
    sheer: t("routineDisclosure.covSheer", "Sheer"),
    medium: t("routineDisclosure.covMedium", "Medium"),
    full: t("routineDisclosure.covFull", "Full"),
    unsure: t("routineDisclosure.covUnsure", "I'm not sure yet"),
  },
  texture: {
    liquid: t("routineDisclosure.texLiquid", "Liquid"),
    cream: t("routineDisclosure.texCream", "Cream"),
    powder: t("routineDisclosure.texPowder", "Powder"),
    unsure: t("routineDisclosure.texUnsure", "I'm not sure yet"),
  },
  lipFinish: {
    cream: t("routineDisclosure.lipCream", "Cream"),
    satin: t("routineDisclosure.lipSatin", "Satin"),
    matte: t("routineDisclosure.lipMatte", "Matte"),
    gloss: t("routineDisclosure.lipGloss", "Gloss"),
    unsure: t("routineDisclosure.lipUnsure", "I'm not sure yet"),
  },
  eyeFinish: {
    matte: t("routineDisclosure.eyeMatte", "Matte"),
    satin: t("routineDisclosure.eyeSatin", "Satin"),
    shimmer: t("routineDisclosure.eyeShimmer", "Shimmer"),
    glitter: t("routineDisclosure.eyeGlitter", "Glitter"),
    unsure: t("routineDisclosure.eyeUnsure", "I'm not sure yet"),
  },
  ethics: {
    cruelty_free: t("routineDisclosure.ethCrueltyFree", "Cruelty-free"),
    vegan: t("routineDisclosure.ethVegan", "Vegan"),
  },
});

const CATEGORY_LABEL = (c) => (c || "").replace(/_/g, " ");

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-3 text-sm py-1.5">
    <span className="text-[#8A7D82] uppercase tracking-[0.18em] text-[10px] shrink-0 w-32">
      {label}
    </span>
    <span className="text-[#2C2123]">{value}</span>
  </div>
);

const RoutineDisclosure = ({ routine, preferences = {} }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const labels = useEnumLabels(t);

  if (!routine) return null;

  const skip = (preferences.skip_categories || []).map(CATEGORY_LABEL).join(", ");
  const ethics = (preferences.ethics || [])
    .map((e) => labels.ethics[e] || e)
    .join(", ");

  return (
    <div
      className="rounded-2xl border border-[#EADDD7] bg-[#FDFBF7] overflow-hidden"
      data-testid="routine-disclosure"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-[#F5EFEB] transition-colors"
        aria-expanded={open}
        data-testid="routine-disclosure-toggle"
      >
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-full bg-white border border-[#EADDD7] flex items-center justify-center">
            <Info className="h-4 w-4 text-[#9B6A6C]" />
          </span>
          <div>
            <div className="text-sm font-medium text-[#2C2123]">
              {t("routineDisclosure.title")}
            </div>
            <div className="text-xs text-[#8A7D82]">
              {t("routineDisclosure.subtitle")}
            </div>
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-[#8A7D82] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-[#EADDD7]">
              {/* What we asked */}
              <div className="mb-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#9B6A6C] flex items-center gap-2 mb-3 mt-3">
                  <Sparkles className="h-3 w-3" />{" "}
                  {t("routineDisclosure.askedHeading")}
                </div>
                <div className="bg-white rounded-xl border border-[#EADDD7] p-4">
                  <Row
                    label={t("routineDisclosure.rowGoal")}
                    value={labels.goal[routine.goal] || routine.goal}
                  />
                  <Row
                    label={t("routineDisclosure.rowExperience")}
                    value={
                      labels.experience[routine.experience] || routine.experience
                    }
                  />
                  <Row
                    label={t("routineDisclosure.rowBeardShadow")}
                    value={
                      routine.beard_shadow
                        ? t("routineDisclosure.beardInclude")
                        : t("routineDisclosure.beardSkip")
                    }
                  />
                  {preferences.coverage && preferences.coverage !== "unsure" && (
                    <Row
                      label={t("routineDisclosure.rowCoverage")}
                      value={labels.coverage[preferences.coverage]}
                    />
                  )}
                  {preferences.texture && preferences.texture !== "unsure" && (
                    <Row
                      label={t("routineDisclosure.rowTexture")}
                      value={labels.texture[preferences.texture]}
                    />
                  )}
                  {preferences.lip_finish &&
                    preferences.lip_finish !== "unsure" && (
                      <Row
                        label={t("routineDisclosure.rowLipFinish")}
                        value={labels.lipFinish[preferences.lip_finish]}
                      />
                    )}
                  {preferences.eye_finish &&
                    preferences.eye_finish !== "unsure" && (
                      <Row
                        label={t("routineDisclosure.rowEyeFinish")}
                        value={labels.eyeFinish[preferences.eye_finish]}
                      />
                    )}
                  {skip && (
                    <Row
                      label={t("routineDisclosure.rowSkipped")}
                      value={skip}
                    />
                  )}
                  {ethics && (
                    <Row
                      label={t("routineDisclosure.rowEthics")}
                      value={ethics}
                    />
                  )}
                  {preferences.notes && (
                    <Row
                      label={t("routineDisclosure.rowYourNote")}
                      value={preferences.notes}
                    />
                  )}
                </div>
              </div>

              {/* How we kept it safe */}
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#9B6A6C] flex items-center gap-2 mb-3">
                  <Shield className="h-3 w-3" />{" "}
                  {t("routineDisclosure.safetyHeading")}
                </div>
                <div className="bg-white rounded-xl border border-[#EADDD7] p-4 space-y-3 text-sm text-[#2C2123] leading-relaxed">
                  <p>
                    <Trans
                      i18nKey="routineDisclosure.safetyIntro"
                      components={{ strong: <span className="font-medium" /> }}
                    />
                  </p>
                  <p>
                    <Trans
                      i18nKey="routineDisclosure.safetyChecked"
                      components={{ strong: <span className="font-medium" /> }}
                    />
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-[#5C4B51]">
                    <li>{t("routineDisclosure.safety1")}</li>
                    <li>{t("routineDisclosure.safety2")}</li>
                    <li>
                      <Trans
                        i18nKey="routineDisclosure.safety3"
                        components={{ em: <em /> }}
                      />
                    </li>
                    <li>{t("routineDisclosure.safety4")}</li>
                    <li>{t("routineDisclosure.safety5")}</li>
                  </ul>
                  <p className="text-xs text-[#8A7D82] pt-1">
                    <Trans
                      i18nKey="routineDisclosure.safetyFooter"
                      components={{
                        fb: <span className="text-[#5D4050]" />,
                      }}
                    />
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RoutineDisclosure;
